import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Plus, Pencil, Search, Users, ExternalLink, Copy, Power, ArrowLeft, X, Upload, MapPin, Database, Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { api, formatApiError, API, TOKEN_KEY } from "../lib/api";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const empty = {
    name: "", email: "", password: "", phone: "",
    license_states: [], zip_codes: [],
    calendly_link: "", photo_url: "", bio: "", website_url: "", is_active: true,
};

export default function AdminAgents() {
    const navigate = useNavigate();
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null); // null | "new" | agent obj
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [zipsRaw, setZipsRaw] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/agents");
            setAgents(data.agents);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const startNew = () => {
        setForm(empty); setZipsRaw(""); setEditing("new");
    };
    const startEdit = (a) => {
        setForm({
            name: a.name, email: a.email, password: "", phone: a.phone || "",
            license_states: a.license_states || [], zip_codes: a.zip_codes || [],
            calendly_link: a.calendly_link || "", photo_url: a.photo_url || "",
            bio: a.bio || "", website_url: a.website_url || "", is_active: !!a.is_active,
        });
        setZipsRaw((a.zip_codes || []).join(", "));
        setEditing(a);
    };

    const toggleState = (s) => {
        setForm((f) => ({
            ...f,
            license_states: f.license_states.includes(s)
                ? f.license_states.filter((x) => x !== s)
                : [...f.license_states, s],
        }));
    };

    const onPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/admin/agent-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
            setForm((f) => ({ ...f, photo_url: fullUrl }));
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const save = async () => {
        const zips = zipsRaw.split(/[\s,]+/).map((z) => z.trim()).filter(Boolean);
        const payload = { ...form, zip_codes: zips };
        if (!payload.name?.trim()) { toast.error("Name required"); return; }
        setSaving(true);
        try {
            if (editing === "new") {
                if (!payload.email || !payload.password) { toast.error("Email + password required"); setSaving(false); return; }
                await api.post("/admin/agents", payload);
                toast.success("Agent created");
            } else {
                const { email: _e, ...rest } = payload;
                if (!rest.password) delete rest.password;
                await api.patch(`/admin/agents/${editing.id}`, rest);
                toast.success("Agent updated");
            }
            setEditing(null);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (a) => {
        try {
            if (a.is_active) {
                await api.delete(`/admin/agents/${a.id}`);
                toast.success(`${a.name} deactivated`);
            } else {
                await api.patch(`/admin/agents/${a.id}`, { is_active: true });
                toast.success(`${a.name} activated`);
            }
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const copyLandingUrl = (slug) => {
        const url = `${window.location.origin}/agent/${slug}`;
        navigator.clipboard.writeText(url);
        toast.success("Landing URL copied");
    };

    const seedDemo = async () => {
        try {
            const { data } = await api.post("/admin/demo/seed");
            toast.success(`Demo loaded: ${data.agents_total} agents, ${data.leads_total} leads`);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const resetDemo = async () => {
        if (!window.confirm("Remove all demo agents and demo leads? Real data is not affected.")) return;
        try {
            const { data } = await api.post("/admin/demo/reset");
            toast.success(`Removed ${data.agents_deleted} demo agents and ${data.leads_deleted} demo leads`);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const filtered = agents.filter((a) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q) || (a.phone || "").includes(q);
    });

    return (
        <div className="min-h-screen bg-slate-50" data-testid="admin-agents-page">
            <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="outline" onClick={() => navigate("/admin")} data-testid="back-to-leads" className="rounded-md">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Leads
                        </Button>
                        <div>
                            <div className="font-serif text-xl text-navy-900">Agents</div>
                            <div className="text-sm text-slate-700">Manage agent accounts and territories</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={seedDemo} data-testid="seed-demo-btn" className="rounded-md" title="Load 3 sample agents + 12 sample leads for demos">
                            <Database className="w-4 h-4 mr-2" /> Load Demo Data
                        </Button>
                        <Button variant="outline" onClick={resetDemo} data-testid="reset-demo-btn" className="rounded-md text-red-700 hover:text-red-800" title="Remove demo data only — real data is untouched">
                            <Trash2 className="w-4 h-4 mr-2" /> Reset Demo
                        </Button>
                        <Button onClick={startNew} data-testid="new-agent-btn" className="bg-navy-900 hover:bg-navy-800 text-white rounded-md">
                            <Plus className="w-4 h-4 mr-2" /> New Agent
                        </Button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-10">
                <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6">
                    <div className="flex items-center gap-3">
                        <div className="relative flex-1 max-w-md">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                            <Input data-testid="agent-search" placeholder="Search agents..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-11" />
                        </div>
                        <div className="text-sm text-slate-700 ml-auto"><Users className="w-4 h-4 inline mr-1" />{filtered.length}</div>
                    </div>

                    <div className="mt-6 overflow-x-auto">
                        <Table data-testid="agents-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Phone</TableHead>
                                    <TableHead>States</TableHead>
                                    <TableHead>ZIPs</TableHead>
                                    <TableHead>Leads</TableHead>
                                    <TableHead>Sold</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading && <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-700">Loading…</TableCell></TableRow>}
                                {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-700">No agents yet. Click "New Agent" to add one.</TableCell></TableRow>}
                                {filtered.map((a) => (
                                    <TableRow key={a.id} data-testid={`agent-row-${a.id}`}>
                                        <TableCell className="font-medium text-navy-900">
                                            <div className="flex items-center gap-3">
                                                {a.photo_url ? (
                                                    <img src={a.photo_url} alt={a.name} className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-navy-900 text-white flex items-center justify-center text-xs font-semibold">{(a.name || "?").slice(0, 1)}</div>
                                                )}
                                                <span>{a.name}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-slate-700">{a.email}</TableCell>
                                        <TableCell className="text-slate-700">{a.phone || "—"}</TableCell>
                                        <TableCell className="text-slate-700 text-sm">{(a.license_states || []).join(", ") || "—"}</TableCell>
                                        <TableCell className="text-slate-700 text-sm">{(a.zip_codes || []).length} ZIP{(a.zip_codes || []).length === 1 ? "" : "s"}</TableCell>
                                        <TableCell className="text-slate-700">{a.lead_count}</TableCell>
                                        <TableCell className="text-slate-700">{a.sold_count}</TableCell>
                                        <TableCell>
                                            <Badge className={a.is_active ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-slate-200 text-slate-700 border border-slate-300"}>
                                                {a.is_active ? "Active" : "Inactive"}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 justify-end">
                                                <Button variant="ghost" size="sm" onClick={() => copyLandingUrl(a.slug)} data-testid={`copy-url-${a.id}`} title="Copy landing URL">
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                                <a href={`/agent/${a.slug}`} target="_blank" rel="noreferrer" data-testid={`view-public-${a.id}`} title="Open landing page">
                                                    <Button variant="ghost" size="sm"><ExternalLink className="w-4 h-4" /></Button>
                                                </a>
                                                <Button variant="ghost" size="sm" onClick={() => startEdit(a)} data-testid={`edit-agent-${a.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => toggleActive(a)} data-testid={`toggle-active-${a.id}`}>
                                                    <Power className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="agent-form-sheet">
                    <SheetHeader>
                        <SheetTitle className="font-serif text-2xl text-navy-900">{editing === "new" ? "New Agent" : `Edit: ${editing?.name}`}</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6 space-y-5">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Name *">
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agent-name" />
                            </Field>
                            <Field label="Phone">
                                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="agent-phone" />
                            </Field>
                            <Field label="Email *">
                                <Input type="email" value={form.email} disabled={editing !== "new"} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="agent-email" />
                            </Field>
                            <Field label={editing === "new" ? "Password *" : "New password (optional)"}>
                                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="agent-password" />
                            </Field>
                            <Field label="Calendly Link">
                                <Input value={form.calendly_link} onChange={(e) => setForm({ ...form, calendly_link: e.target.value })} placeholder="https://calendly.com/..." data-testid="agent-calendly" />
                            </Field>
                            <Field label="Company Website">
                                <Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://yourcompany.com" data-testid="agent-website" />
                            </Field>
                            <Field label="Status">
                                <div className="flex items-center gap-3 mt-2">
                                    <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="agent-active" />
                                    <span className="text-sm text-slate-600">{form.is_active ? "Active" : "Inactive"}</span>
                                </div>
                            </Field>
                        </div>

                        <Field label="Bio">
                            <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Short bio shown on agent landing page" data-testid="agent-bio" />
                        </Field>

                        <Field label="Profile Photo">
                            <div className="flex items-center gap-4">
                                {form.photo_url ? (
                                    <img src={form.photo_url} alt="" className="w-16 h-16 rounded-full object-cover border border-slate-200" />
                                ) : (
                                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                                        <Upload className="w-5 h-5" />
                                    </div>
                                )}
                                <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-slate-200 rounded-md text-sm font-medium hover:bg-slate-50">
                                    <Upload className="w-4 h-4 mr-2" /> {form.photo_url ? "Replace" : "Upload"}
                                    <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="agent-photo-upload" />
                                </label>
                                {form.photo_url && (
                                    <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, photo_url: "" })} data-testid="agent-photo-clear">Clear</Button>
                                )}
                            </div>
                        </Field>

                        <Field label="Licensed States">
                            <div className="flex flex-wrap gap-2 mt-2 max-h-44 overflow-y-auto p-3 bg-slate-50 border border-slate-200 rounded-md">
                                {STATES.map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => toggleState(s)}
                                        data-testid={`state-${s}`}
                                        className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${form.license_states.includes(s) ? "bg-navy-900 text-white border-navy-900" : "bg-white text-slate-700 border-slate-200 hover:border-navy-300"}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                            <div className="mt-2 text-xs text-slate-700">{form.license_states.length} state{form.license_states.length === 1 ? "" : "s"} selected</div>
                        </Field>

                        <Field label="ZIP Codes (comma or space separated)">
                            <Textarea rows={3} value={zipsRaw} onChange={(e) => setZipsRaw(e.target.value)} placeholder="33101, 33102, 33103" data-testid="agent-zips" />
                            <div className="mt-1 text-xs text-slate-700"><MapPin className="w-3 h-3 inline mr-1" />Leads from these ZIPs route here first; falls back to license-state match.</div>
                        </Field>

                        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                            <Button variant="outline" onClick={() => setEditing(null)} data-testid="agent-cancel">Cancel</Button>
                            <Button onClick={save} disabled={saving} className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="agent-save">
                                {saving ? "Saving..." : (editing === "new" ? "Create Agent" : "Save Changes")}
                            </Button>
                        </div>
                    </div>

                    <button onClick={() => setEditing(null)} className="absolute top-4 right-4 text-slate-600 hover:text-slate-700">
                        <X className="w-5 h-5" />
                    </button>
                </SheetContent>
            </Sheet>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-sm text-slate-700">{label}</Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
